import { DatePipe, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BackendService, DialogService } from '@core/services';
import { BaseComponent, DialogRef } from '@shared/classes';
import { ButtonComponent } from '@shared/components/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TermsAndConditionsModalComponent } from '@shared/components/modals/terms-and-conditions-modal/terms-and-conditions-modal.component';
import { RewardCode } from '@shared/constants';
import { Reward } from '@shared/factory';
import {
  BurnRewardDialog,
  DIALOG_DATA,
  DialogConfig,
  RewardCodeType,
} from '@shared/models';
import { SafePipe, VariableReplacementPipe } from '@shared/pipes';
import { detectJsBarcodeFormat } from '@shared/utils';

@Component({
  selector: 'b2c-burn-reward-modal',
  styleUrl: './burn-reward-modal.component.scss',
  templateUrl: './burn-reward-modal.component.html',
  imports: [
    SafePipe,
    DatePipe,
    IconComponent,
    ButtonComponent,
    VariableReplacementPipe,
  ],
})
export class BurnRewardModalComponent
  extends BaseComponent
  implements AfterViewInit
{
  public config: BurnRewardDialog = <BurnRewardDialog>(
    this.loyaltyChallengeConfig.components.modals.burnReward
  );

  barcode = viewChild<ElementRef<SVGElement>>('barcode');
  qrcode = viewChild<ElementRef<HTMLDivElement>>('qrcode');
  pdfContent = viewChild.required<ElementRef<HTMLDivElement>>('pdfContent');

  public showError = signal(false);
  public selectionInProgress = signal(false);
  public readonly RewardCode: typeof RewardCode = RewardCode;
  public data: Reward = inject<DialogConfig>(DIALOG_DATA).data as Reward;

  private readonly document: Document = inject(DOCUMENT);
  private readonly dialogRef: DialogRef = inject(DialogRef);
  private readonly dialog: DialogService = inject(DialogService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly backendService: BackendService = inject(BackendService);

  ngAfterViewInit(): void {
    if (this.data.codeDetails) {
      this.generateBarcodes(this.data.codeDetails);
    }
  }

  handleSelection(): void {
    if (
      this.selectionInProgress() ||
      this.showError() ||
      this.dataService.totalPoints() < this.data.priceToUnlock
    ) {
      return;
    }

    this.showError.set(false);
    this.selectionInProgress.set(true);

    if (this.data.isActive) {
      this.closeModal();
    } else {
      this.backendService
        .activateReward(this.data.rewardId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (result: Reward) => {
            if (result.activationsLeft <= 0) {
              // if the reward has no activations left, remove it from the list of rewards
              this.dataService.rewards.set(
                this.dataService
                  .rewards()
                  .filter(
                    (reward: Reward) => reward.rewardId !== this.data.rewardId
                  )
              );
            }

            // deduct the points used to activate the reward
            this.dataService.totalPoints.update(
              (points) => points - this.data.priceToUnlock
            );

            // add the reward to the list of purchased rewards
            this.dataService.purchasedRewards.set([
              ...this.dataService.purchasedRewards(),
              result,
            ]);
            this.dataService.reloadSlider.update((value) => value + 1);
            this.selectionInProgress.set(false);
            this.closeModal();
            this.dataService.rewardPageActiveTab.set(1); // switch to purchased rewards tab
          },
          error: () => {
            this.showError.set(true);
            this.selectionInProgress.set(false);
            this.cdr.detectChanges();
            console.error('Error activating reward');
          },
        });
    }
  }

  async handleExport(): Promise<void> {
    const element = this.pdfContent().nativeElement;
    const clonedElement = this.cloneElementForExport(element);

    try {
      await this.prepareElementForCanvasCapture(element, clonedElement);
      const pdfName = this.formatPdfName(this.data.label);
      await this.createPdfFromElement(clonedElement, pdfName);
    } catch (error) {
      console.error('Error exporting reward PDF', error);
    } finally {
      this.document.body.removeChild(clonedElement);
    }
  }

  showTermsAndConditions(): void {
    this.dialog.open(TermsAndConditionsModalComponent, {
      data: this.data.termsAndConditions,
    });
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  private async generateBarcodes(codeDetails: {
    type: RewardCodeType;
    value: string;
  }): Promise<void> {
    if (codeDetails.type === RewardCode.BARCODE) {
      const element = this.barcode()?.nativeElement;

      const value = codeDetails.value;
      const pick = detectJsBarcodeFormat(value, {
        // set to true if you want 0‑prefixed EAN‑13s rendered as UPC‑A
        preferUPCWhenEan13StartsWithZero: true,
        strict: true,
      });

      if (!pick) {
        throw new Error('Unsupported/invalid code');
      }

      try {
        const { default: JsBarcode } = await import('jsbarcode');
        JsBarcode(element, pick.data, {
          width: 2,
          height: 40,
          lineColor: 'black',
          displayValue: true,
          format: pick.format,
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    } else if (codeDetails.type === RewardCode.QRCODE) {
      const element = this.qrcode()?.nativeElement;
      const QRCode = await import('qrcode');
      QRCode.toCanvas(element, codeDetails.value, {
        margin: 0,
        width: 150,
      })
        .then(() => {
          console.log('QR code generated!');
        })
        .catch((err) => {
          console.error('Error generating QR code:', err);
        });
    }
  }

  private cloneElementForExport(element: HTMLElement): HTMLElement {
    const clonedElement = element.cloneNode(true) as HTMLElement;

    this.applyBaseStylesForExport(clonedElement, element.offsetWidth);
    this.revealTermsForExport(clonedElement);
    this.document.body.appendChild(clonedElement);

    return clonedElement;
  }

  private applyBaseStylesForExport(element: HTMLElement, width: number): void {
    element.style.top = '0';
    element.style.zIndex = '-1';
    element.style.left = '-9999px';
    element.style.position = 'fixed';
    element.style.textAlign = 'center';
    element.style.pointerEvents = 'none';
    element.style.width = `${width}px`;
  }

  private revealTermsForExport(element: HTMLElement): void {
    const clonedTerms = element.querySelector(
      '[data-terms-print="true"]'
    ) as HTMLElement | null;
    if (clonedTerms) {
      clonedTerms.style.visibility = 'visible';
      clonedTerms.style.height = 'auto';
      clonedTerms.style.marginTop = '16px';
      clonedTerms.style.padding = '0 16px';
      clonedTerms.style.overflow = 'visible';
      clonedTerms.style.display = 'block';
      clonedTerms.style.whiteSpace = 'pre-wrap';
      clonedTerms.style.textAlign = 'left';
      clonedTerms.style.fontSize = '13px';
      clonedTerms.style.color = '#4d4d4d';
      clonedTerms.removeAttribute('aria-hidden');
    }
  }

  private async prepareElementForCanvasCapture(
    sourceElement: HTMLElement,
    clonedElement: HTMLElement
  ): Promise<void> {
    this.replaceCanvasWithInlineImage(sourceElement, clonedElement);
    this.replaceSvgWithInlineImage(clonedElement);
    this.setAnonymousCrossOriginForImages(clonedElement);

    await this.waitForImagesToLoad(clonedElement);
  }

  private async createPdfFromElement(
    element: HTMLElement,
    pdfName: string
  ): Promise<void> {
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import('html2canvas'),
      import('jspdf'),
    ]);

    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: false,
    });

    const pdf = new jsPDF('p', 'mm', 'a4'); // A4 size page of PDF
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const widthRatio = pageWidth / canvas.width;
    const heightRatio = pageHeight / canvas.height;
    const ratio = Math.min(widthRatio, heightRatio);
    const imgWidth = canvas.width * ratio;
    const imgHeight = canvas.height * ratio;
    const xPosition = (pageWidth - imgWidth) / 2;
    const contentDataURL = canvas.toDataURL('image/png');
    pdf.addImage(contentDataURL, 'PNG', xPosition, 0, imgWidth, imgHeight);
    pdf.save(pdfName + '.pdf');
  }

  private formatPdfName(label: string): string {
    return label.replace(/\s+/g, '_').toLowerCase();
  }

  /**
   * set crossOrigin attribute to anonymous for all images within the given element
   * this will help to avoid CORS issues when exporting the element as PDF
   * @param element The parent HTMLElement containing images
   */
  private setAnonymousCrossOriginForImages(element: HTMLElement): void {
    const images = Array.from(
      element.querySelectorAll<HTMLImageElement>('img')
    );

    images.forEach((image: HTMLImageElement) => {
      if (!image.src) {
        return;
      }

      image.crossOrigin = 'anonymous';
      image.setAttribute('crossorigin', 'anonymous');

      const currentSrc = image.src;
      image.removeAttribute('src');
      image.src = currentSrc;
    });
  }

  private replaceSvgWithInlineImage(element: HTMLElement): void {
    const svgs = Array.from(element.querySelectorAll<SVGElement>('svg'));

    svgs.forEach((svgElement: SVGElement) => {
      const serializer = new XMLSerializer();
      const svgClone = svgElement.cloneNode(true) as SVGElement;
      const svgString = serializer.serializeToString(svgClone);
      const encodedSvg = encodeURIComponent(svgString)
        .replace(/%0A/g, '')
        .replace(/%0D/g, '');

      const imgElement = this.document.createElement('img');
      imgElement.src = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;
      imgElement.alt =
        svgElement.getAttribute('aria-label') ?? svgElement.id ?? 'reward-code';

      const { width, height } = svgElement.getBoundingClientRect();
      if (width) {
        imgElement.style.width = `${width}px`;
      }
      if (height) {
        imgElement.style.height = `${height}px`;
      }

      svgElement.replaceWith(imgElement);
    });
  }

  private replaceCanvasWithInlineImage(
    originalElement: HTMLElement,
    clonedElement: HTMLElement
  ): void {
    const originalCanvases = Array.from(
      originalElement.querySelectorAll<HTMLCanvasElement>('canvas')
    );
    const clonedCanvases = Array.from(
      clonedElement.querySelectorAll<HTMLCanvasElement>('canvas')
    );

    clonedCanvases.forEach((clonedCanvas, index) => {
      const sourceCanvas = originalCanvases[index];
      if (!sourceCanvas) {
        return;
      }

      const dataUrl = sourceCanvas.toDataURL('image/png');
      const imgElement = this.document.createElement('img');
      imgElement.src = dataUrl;
      imgElement.alt =
        clonedCanvas.getAttribute('aria-label') ??
        clonedCanvas.id ??
        'reward-code';

      const { width, height } = sourceCanvas.getBoundingClientRect();
      if (width) {
        imgElement.style.width = `${width}px`;
      }
      if (height) {
        imgElement.style.height = `${height}px`;
      }

      clonedCanvas.replaceWith(imgElement);
    });
  }

  private async waitForImagesToLoad(element: HTMLElement): Promise<void> {
    const images = Array.from(
      element.querySelectorAll<HTMLImageElement>('img')
    );

    const loaders = images.map((image) => {
      if (image.complete && image.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise<void>((resolve) => {
        const onLoad = () => {
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onLoad);
          resolve();
        };

        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onLoad, { once: true });
      });
    });

    await Promise.all(loaders);
  }
}
